import React, { useEffect, useState, useRef } from 'react';
import { BASE_DOMAIN, isBaseDomain } from '../utility/baseDomain';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { fetchDomains, deleteDomain, DomainName } from '../store/slices/domainsSlice';
import { fetchInstances, updateInstance } from '../store/slices/instancesSlice';
import { useToast } from '../components/ToastContext';
import { useConfirm } from '../components/ConfirmContext';
import axiosInstance from '../utility/axiosInstance';
import { handleAxiosError } from '../helpers/handleAxiosError';
import { deleteDnsEntry, instanceForName, DnsEntryInstance } from '../helpers/deleteDnsEntry';
import '../styles/DomainsPage.scss';

type AddStep = 'idle' | 'loading-challenge' | 'challenge' | 'verifying';

/** GET /v1/dns/verify-domain: the record for THIS account (ROADMAP 91), and a hint when its value changed. */
interface Challenge { record_name: string; record_value: string; hint?: string }

const POLL_INTERVAL_MS = 60_000;

/** The API's answer as the toast shows it (its message and hint), or `fallback` when there is none. */
const reasonOf = (e: any, fallback: string) => {
    const data = e?.response?.data;
    return data && typeof data === 'object' ? data : fallback;
};

const DomainsPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { domains, loading } = useSelector((s: RootState) => s.domains);
    const instances = useSelector((s: RootState) => s.instances.data) as DnsEntryInstance[];
    const instancesStatus = useSelector((s: RootState) => s.instances.status);
    const { showToast } = useToast();
    const { confirm } = useConfirm();

    const [newDomain, setNewDomain] = useState('');
    const [addStep, setAddStep] = useState<AddStep>('idle');
    const [challenge, setChallenge] = useState<Challenge | null>(null);
    const [deletingDomain, setDeletingDomain] = useState<string | null>(null);
    const [manualVerifying, setManualVerifying] = useState<string | null>(null);
    const [challenges, setChallenges] = useState<Record<string, Challenge>>({});
    // A pending domain another account has verified (its record request answers DOMAIN_TAKEN): never polled again.
    const [taken, setTaken] = useState<Record<string, string>>({});
    const takenRef = useRef<Record<string, string>>({});
    // ROADMAP 86: a verified domain whose removal was refused, with the names of yours that still use it.
    const [inUse, setInUse] = useState<Record<string, DomainName[]>>({});
    const [removingName, setRemovingName] = useState<number | null>(null);

    const pollRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        dispatch(fetchDomains());
    }, [dispatch]);

    // Auto-poll pending domains every 60s
    useEffect(() => {
        const pending = domains.filter(d => d.status === 'pending');
        if (pending.length === 0) {
            if (pollRef.current) clearInterval(pollRef.current);
            return;
        }

        // Fetch challenges for pending domains we don't have yet
        pending.forEach(d => {
            if (!challenges[d.domain] && !takenRef.current[d.domain]) {
                axiosInstance.get(`/v1/dns/verify-domain?domain=${encodeURIComponent(d.domain)}`)
                    .then(res => setChallenges(prev => ({ ...prev, [d.domain]: res.data })))
                    .catch((err) => {
                        // Another account verified it first: say so instead of waiting for a record forever.
                        const e = err?.response?.data?.error;
                        if (err?.response?.status === 409 && e?.code === 'DOMAIN_TAKEN') {
                            const why = typeof e.message === 'string' ? e.message : 'This domain is already verified by another account.';
                            takenRef.current = { ...takenRef.current, [d.domain]: why };
                            setTaken(takenRef.current);
                        }
                        /* otherwise the challenge fetch is best-effort */
                    });
            }
        });

        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            const stillPending = domains.filter(d => d.status === 'pending' && !takenRef.current[d.domain]);
            for (const d of stillPending) {
                try {
                    await axiosInstance.post('/v1/dns/verify-domain', { domain: d.domain });
                    showToast(`✓ ${d.domain} verified automatically`, 'success');
                    dispatch(fetchDomains());
                } catch (_err) { /* not verified yet, will retry */ }
            }
        }, POLL_INTERVAL_MS);

        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [domains]);

    const fqdnPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

    const handleGetChallenge = async () => {
        if (!newDomain.trim()) return;
        if (isBaseDomain(newDomain)) {
            showToast(`${BASE_DOMAIN} domains are managed automatically.`, 'error');
            return;
        }
        if (!fqdnPattern.test(newDomain)) {
            showToast('Enter a valid domain name (e.g. app.yourcompany.com)', 'error');
            return;
        }
        setAddStep('loading-challenge');
        try {
            const res = await axiosInstance.get(`/v1/dns/verify-domain?domain=${encodeURIComponent(newDomain)}`);
            setChallenge(res.data);
            setAddStep('challenge');
            dispatch(fetchDomains()); // will now show the pending row
        } catch (e: any) {
            showToast(reasonOf(e, 'Failed to load verification challenge'), 'error');
            setAddStep('idle');
        }
    };

    const handleManualVerify = async (domain: string) => {
        setManualVerifying(domain);
        try {
            await axiosInstance.post('/v1/dns/verify-domain', { domain });
            showToast(`${domain} verified`, 'success');
            dispatch(fetchDomains());
        } catch (e: any) {
            showToast(reasonOf(e, 'Not verified yet - DNS can take up to 24h to propagate.'), 'error');
        } finally {
            setManualVerifying(null);
        }
    };

    const handleVerifyNew = async () => {
        if (!challenge) return;
        setAddStep('verifying');
        try {
            await axiosInstance.post('/v1/dns/verify-domain', { domain: newDomain });
            showToast(`${newDomain} verified`, 'success');
            dispatch(fetchDomains());
            setNewDomain('');
            setChallenge(null);
            setAddStep('idle');
        } catch (e: any) {
            showToast(reasonOf(e, 'Not verified yet - DNS can take up to 24h to propagate.'), 'error');
            setAddStep('challenge');
        }
    };

    const closeInUse = (domain: string) => setInUse(prev => {
        const next = { ...prev };
        delete next[domain];
        return next;
    });

    // Cancel a pending domain, or remove a verified one. ROADMAP 86: a verified domain is not removed while names of yours
    // still use it; the API lists them, and they are shown under the domain, each removable, until it is removed again.
    const handleDelete = async (domain: string) => {
        setDeletingDomain(domain);
        try {
            const result = await dispatch(deleteDomain(domain));
            if (deleteDomain.fulfilled.match(result)) {
                closeInUse(domain);
                showToast(`${domain} removed`, 'success');
                return;
            }
            const refusal = result.payload;
            if (refusal?.code === 'DOMAIN_IN_USE') {
                setInUse(prev => ({ ...prev, [domain]: refusal.names }));
                dispatch(fetchInstances());     // which instance each name is on, for its Remove
                showToast(refusal.message, 'error');
                return;
            }
            showToast(refusal?.message || 'Failed to remove domain', 'error');
        } finally {
            setDeletingDomain(null);
        }
    };

    // The console's DNS-entry delete (helpers/deleteDnsEntry): the instance's record of the name, then the name.
    const handleRemoveName = async (domain: string, name: DomainName, instance: DnsEntryInstance) => {
        const proceed = await confirm({
            title: 'Delete DNS Record',
            message: `Delete ${name.domain_names.join(', ')}? It stops serving.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;
        setRemovingName(name.id);
        try {
            const { dns, metadataDns } = await deleteDnsEntry(name.id, instance);
            // The instance's entries as they are now, kept here at once: the next removal computes from them (a refetch
            // racing it could hand back the lists from before, and the entry just removed would be written back).
            const current = instances.find(i => i.id === instance.id);
            if (current) dispatch(updateInstance({ ...(current as any), dns, metadata: { ...(current.metadata ?? {}), dns: metadataDns ?? undefined } }));
            setInUse(prev => (prev[domain] ? { ...prev, [domain]: prev[domain].filter(n => n.id !== name.id) } : prev));
            showToast('DNS deleted!', 'success');
        } catch (err) {
            showToast(handleAxiosError(err) || 'Failed to delete DNS!', 'error');
        } finally {
            setRemovingName(null);
        }
    };

    const pendingDomains = domains.filter(d => d.status === 'pending');
    const verifiedDomains = domains
        .filter(d => d.status === 'verified')
        .slice()
        .sort((a, b) => {
            const ta = a.verified_at ? new Date(a.verified_at).getTime() : 0;
            const tb = b.verified_at ? new Date(b.verified_at).getTime() : 0;
            return tb - ta;
        });

    const renderInUse = (domain: string, names: DomainName[]) => (
        <div className="in-use-panel">
            <p className="in-use-intro">
                {names.length > 0
                    ? `These names still use ${domain}. Remove each one, then remove the domain again.`
                    : `No names use ${domain} now. Remove the domain again.`}
            </p>
            {names.map(n => {
                const instance = instanceForName(instances, n);
                return (
                    <div key={n.id} className="in-use-name">
                        <code className="in-use-domains">{n.domain_names.join(', ')}</code>
                        {n.forward_host && <span className="in-use-target">{n.forward_host}</span>}
                        {instance ? (
                            <button
                                className="btn-danger-sm"
                                onClick={() => handleRemoveName(domain, n, instance)}
                                disabled={removingName !== null}
                            >
                                {removingName === n.id ? 'Removing…' : 'Remove'}
                            </button>
                        ) : instancesStatus === 'loading' || instancesStatus === 'idle' ? (
                            <span className="in-use-elsewhere">Finding its instance…</span>
                        ) : (
                            <span className="in-use-elsewhere">Not on one of your instances: remove it on its load balancer or cluster.</span>
                        )}
                    </div>
                );
            })}
            <div className="in-use-actions">
                <button className="btn-secondary" onClick={() => closeInUse(domain)}>Close</button>
                <button className="btn-primary" onClick={() => handleDelete(domain)} disabled={deletingDomain === domain}>
                    {deletingDomain === domain ? 'Removing…' : 'Remove domain again'}
                </button>
            </div>
        </div>
    );

    return (
        <div className="domains-page">
            <div className="page-header">
                <h1 className="page-title">Custom Domains</h1>
                <p className="page-subtitle">
                    Verify a domain before attaching it to an instance.
                </p>
            </div>

            {/* Add domain */}
            <div className="domain-card add-card">
                <div className="card-header">Add a custom domain</div>
                <div className="card-body">
                    {addStep === 'idle' || addStep === 'loading-challenge' ? (
                        <div className="add-form">
                            <input
                                className="domain-input"
                                type="text"
                                value={newDomain}
                                onChange={e => setNewDomain(e.target.value)}
                                placeholder="app.yourcompany.com"
                                onKeyDown={e => e.key === 'Enter' && handleGetChallenge()}
                            />
                            <button
                                className="btn-primary"
                                onClick={handleGetChallenge}
                                disabled={addStep === 'loading-challenge' || !newDomain.trim()}
                            >
                                {addStep === 'loading-challenge' ? 'Loading…' : 'Get verification record'}
                            </button>
                        </div>
                    ) : (
                        <div className="challenge-block">
                            <p className="challenge-intro">
                                Add this TXT record, then verify.
                            </p>
                            <ChallengeBox
                                record_name={challenge!.record_name}
                                record_value={challenge!.record_value}
                                onCopy={(v) => { navigator.clipboard.writeText(v); showToast('Copied', 'success'); }}
                            />
                            {challenge?.hint && <p className="challenge-hint">{challenge.hint}</p>}
                            <div className="challenge-actions">
                                <button className="btn-secondary" onClick={() => { setNewDomain(''); setChallenge(null); setAddStep('idle'); }}>Close</button>
                                <button className="btn-primary" onClick={handleVerifyNew} disabled={addStep === 'verifying'}>
                                    {addStep === 'verifying' ? 'Checking…' : 'Verify now'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Pending domains */}
            {pendingDomains.length > 0 && (
                <div className="domain-card">
                    <div className="card-header">
                        Pending verification
                        <span className="auto-poll-note">Checking automatically every minute</span>
                    </div>
                    <div className="card-body">
                        {pendingDomains.map(d => (
                            <div key={d.domain} className="pending-row">
                                <div className="pending-domain-name">{d.domain}</div>
                                <div className="pending-challenge">
                                    {challenges[d.domain] ? (
                                        <>
                                            <ChallengeBox
                                                record_name={challenges[d.domain].record_name}
                                                record_value={challenges[d.domain].record_value}
                                                onCopy={(v) => { navigator.clipboard.writeText(v); showToast('Copied', 'success'); }}
                                                compact
                                            />
                                            {challenges[d.domain].hint && <p className="challenge-hint">{challenges[d.domain].hint}</p>}
                                        </>
                                    ) : taken[d.domain] ? (
                                        <p className="challenge-taken">{taken[d.domain]}</p>
                                    ) : (
                                        <p className="challenge-loading">Loading TXT record…</p>
                                    )}
                                </div>
                                <div className="pending-actions">
                                    {!taken[d.domain] && (
                                        <button
                                            className="btn-primary"
                                            onClick={() => handleManualVerify(d.domain)}
                                            disabled={manualVerifying === d.domain}
                                        >
                                            {manualVerifying === d.domain ? 'Checking…' : 'Verify now'}
                                        </button>
                                    )}
                                    <button
                                        className="btn-danger-sm"
                                        onClick={() => handleDelete(d.domain)}
                                        disabled={deletingDomain === d.domain}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Verified domains */}
            <div className="domain-card">
                <div className="card-header">Verified domains</div>
                <div className="card-body">
                    {loading && <p className="empty-msg">Loading…</p>}
                    {!loading && verifiedDomains.length === 0 && (
                        <p className="empty-msg">No verified domains yet.</p>
                    )}
                    {!loading && verifiedDomains.length > 0 && (
                        <table className="domains-table">
                            <thead>
                                <tr>
                                    <th>Domain</th>
                                    <th>Verified</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {verifiedDomains.map(d => (
                                    <React.Fragment key={d.domain}>
                                        <tr>
                                            <td className="domain-name">{d.domain}</td>
                                            <td>{d.verified_at ? new Date(d.verified_at).toLocaleDateString() : '-'}</td>
                                            <td>
                                                <button
                                                    className="btn-danger-sm"
                                                    onClick={() => handleDelete(d.domain)}
                                                    disabled={deletingDomain === d.domain}
                                                >
                                                    {deletingDomain === d.domain ? 'Removing…' : 'Remove'}
                                                </button>
                                            </td>
                                        </tr>
                                        {inUse[d.domain] && (
                                            <tr className="in-use-row">
                                                <td colSpan={3}>{renderInUse(d.domain, inUse[d.domain])}</td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

interface ChallengeBoxProps {
    record_name: string;
    record_value: string;
    onCopy: (v: string) => void;
    compact?: boolean;
}
const ChallengeBox: React.FC<ChallengeBoxProps> = ({ record_name, record_value, onCopy, compact }) => (
    <div className={`dns-record-box${compact ? ' compact' : ''}`}>
        <div className="record-row">
            <span className="record-label">Type</span>
            <code className="record-value-code">TXT</code>
        </div>
        <div className="record-row">
            <span className="record-label">Name</span>
            <code className="record-value-code copy-target" onClick={() => onCopy(record_name)} title="Click to copy">
                {record_name} <span className="copy-hint">copy</span>
            </code>
        </div>
        <div className="record-row">
            <span className="record-label">Value</span>
            <code className="record-value-code copy-target" onClick={() => onCopy(record_value)} title="Click to copy">
                {record_value} <span className="copy-hint">copy</span>
            </code>
        </div>
    </div>
);

export default DomainsPage;
